import ActionButton from 'components/ActionButton';
import React, { useEffect, useState } from 'react';
import styles from './index.module.scss';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { Error } from 'components/message/Error';
import { DEFAULT_PLACEHOLDER, showError, showSuccess } from 'utils';
import { createRecipe, fetchRecipe, updateRecipe } from 'services';
import ImageUploadComponent from 'components/ImageUploadComponent';
import { Loading } from 'components/loading/Loading';
import DraggableComponent from './DraggableComponent';
import DropDown from 'components/FormFields/dropdown';

const validationSchema = yup.object().shape({
  name: yup.string().required('Please enter name'),
  enName: yup.string(),
  description: yup.string().required('Please enter description'),
  enDescription: yup.string(),
  image: yup.mixed(),
});

type FormData = {
  name: string;
  enName: string;
  description: string;
  enDescription: string;
  image: any;
  preparationTime: any;
  cookingTime: any;
  nutritionValue: any;
  difficultyLevel: string;
};

const RECIPE_DIFFICULTY_LEVELS = [{ name: '', value: 'Choose Difficulty Level' }, { name: 'Easy', value: 'easy' }, { name: 'Medium', value: 'medium' }, { name: 'Hard', value: 'hard' }];

const CreateRecipe = props => {
  const { recipeId } = props.match.params;
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState() as any;
  const [stepData, setStepData] = useState<string>('');
  const [steps, setSteps] = useState<any>([]);
  const [imageForPreview, setImageForPreview] = useState<string>('');
  const [difficultyLevel, setDifficultyLevel] = useState<string>('');

  const {
    register,
    handleSubmit,
    errors,
    setValue,
  } = useForm<FormData>({ resolver: yupResolver(validationSchema) });

  const fetchRecipeFn = async () => {
    setLoading(true);
    try {
      const response = await fetchRecipe(recipeId);
      const { name, enName, description, enDescription, image, steps, cookingTime, preparationTime, difficultyLevel, nutritionValue } = response;
      setData(response);
      setValue('name', name);
      setValue('enName', enName);
      setValue('description', description);
      setValue('enDescription', enDescription);
      setValue('cookingTime', cookingTime);
      setValue('nutritionValue', nutritionValue);
      setValue('preparationTime', preparationTime);
      setDifficultyLevel(difficultyLevel);
      setSteps(steps);
      setImageForPreview(image && image?.url || DEFAULT_PLACEHOLDER);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    register('image');
    if (recipeId && !data) {
      fetchRecipeFn();
    }
  }, [steps]);

  const saveRecipeFn = handleSubmit(async (values) => {
    const cookingTime = values?.cookingTime ? JSON.parse(values?.cookingTime) : null;
    const preparationTime = values?.preparationTime ? JSON.parse(values?.preparationTime) : null;
    const nutritionValue = values?.nutritionValue ? JSON.parse(values?.nutritionValue) : null;
    setLoading(true);
    try {
      if (recipeId) {
        const res = await updateRecipe({ ...values, steps, id: data?.objectId, cookingTime, nutritionValue, preparationTime, difficultyLevel });
        showSuccess('Recipe updated successfully.');
        props.history.replace(`/recipes/${res?.id}`);
      } else {
        await createRecipe({ ...values, difficultyLevel, steps, cookingTime, nutritionValue, preparationTime });
        showSuccess('Recipe created successfully.');
        props.history.replace('/recipes');
      }
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  });

  const getHeader = () => {
    const heading = recipeId ? 'Update Recipe' : 'Create a New Recipe';
    return (
      <div className={styles['header']}>
        <div className={styles['heading']}>{heading}</div>
      </div>
    );
  };

  const getFooter = () => {
    return (
      <div className={styles['footer']}>
        <ActionButton
          text={'Cancel'}
          active={!loading}
          isLoading={false}
          onClick={() => props.history.goBack()}
          customClass={styles['cancel']}
        />
        <ActionButton
          text={'Save'}
          active={!loading}
          isLoading={loading}
          onClick={saveRecipeFn}
        />
      </div>
    );
  };

  const getFieldError = (error) => {
    if (!error) return null;
    return (
      <Error message={error} />
    );
  };

  const getName = () => {
    return (
      <div className={styles['name']}>
        <div className={styles['label']}>Name*</div>
        <input
          name={'name'}
          ref={register}
          className={'form-control'}
          disabled={loading}
        />
        {getFieldError(errors?.name?.message)}
      </div>
    );
  };

  const getDescription = () => {
    return (
      <div className={styles['description']}>
        <div className={styles['label']}>Description*</div>
        <textarea
          name={'description'}
          ref={register}
          className={'form-control'}
          disabled={loading}
        />
        {getFieldError(errors?.description?.message)}
      </div>
    );
  };

  const getEnName = () => {
    return (
      <div className={styles['name']}>
        <div className={styles['label']}>English Name</div>
        <input
          name={'enName'}
          ref={register}
          className={'form-control'}
          disabled={loading}
        />
        {getFieldError(errors?.enName?.message)}
      </div>
    );
  };

  const getEnDescription = () => {
    return (
      <div className={styles['description']}>
        <div className={styles['label']}>English Description</div>
        <textarea
          name={'enDescription'}
          ref={register}
          className={'form-control'}
          disabled={loading}
        />
        {getFieldError(errors?.enDescription?.message)}
      </div>
    );
  };

  const getRecipeTime = () => {
    return (
      <div className='d-flex'>
        <div className={'mb-2'}>
          <div className={styles['label']}>Preparation Time(min)</div>
          <input
            name={'preparationTime'}
            type={'number'}
            ref={register}
            className={'form-control'}
            disabled={loading}
          />
        </div>
        <div className={'mb-4 ml-5'}>
          <div className={styles['label']}>Cooking Time(min)</div>
          <input
            name={'cookingTime'}
            type={'number'}
            ref={register}
            className={'form-control'}
            disabled={loading}
          />
        </div>
      </div>
    );
  };

  const getRecipeProperties = () => {
    return (
      <div className='d-flex'>
        <div className={'mb-2'}>
          <div className={styles['label']}>Nutrition Value</div>
          <input
            name={'nutritionValue'}
            type={'number'}
            ref={register}
            className={'form-control'}
            disabled={loading}
          />
        </div>
        <div className={'mb-4 ml-5'}>
          <DropDown
            title='Difficulty Level'
            onChange={setDifficultyLevel}
            options={RECIPE_DIFFICULTY_LEVELS}
            value={difficultyLevel}
          />
        </div>
      </div>
    );
  };

  const getSteps = () => {
    return (
      <DraggableComponent
        setSteps={setSteps}
        steps={steps}
        loading={loading}
      />
    );
  };

  const handleStepAdd = () => {
    if (stepData) {
      setSteps([...steps, stepData]);
      setStepData('');
    }
  };

  const getStepsInput = () => {
    return (
      <div className='steps'>
        {getSteps()}
        <div className={styles['description']}>
          <div className={styles['label']}>Steps</div>
          <input
            className={'form-control'}
            disabled={loading}
            value={stepData}
            onChange={(e) => setStepData(e.target.value)}
          />
          <ActionButton
            text={'Add Step'}
            active={true}
            isLoading={false}
            onClick={handleStepAdd}
            customClass={'button mt-1 mr-1'}
          />
        </div>
      </div>
    );
  };

  const onImageUpload = (id, image) => {
    setValue('image', image);
    setImageForPreview(image?._url);
  };

  const getImageInput = () => {
    return (
      <div className={styles['image-container']}>
        <ImageUploadComponent
          src={imageForPreview.toString()}
          uploadAPI={onImageUpload}
          onFileSelect={setImageForPreview}
          forceShowUploadButton
        />
      </div>
    );
  };

  return (
    <div>
      {loading && <Loading />}
      <div className={styles['main-container']}>
        {getHeader()}
        <div className={styles['sub-container']}>
          {getImageInput()}
          <div className={styles['item-details']}>
            {getName()}
            {getDescription()}
            {getEnName()}
            {getEnDescription()}
            {getRecipeTime()}
            {getRecipeProperties()}
            {getStepsInput()}
          </div>
        </div>
        {getFooter()}
      </div>
    </div>
  );
};

export { CreateRecipe };
